import React from 'react'

import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import {
  DcBreadcrumbs,
  DcButton,
  DcCheckbox,
  DcDemandForecastCard,
  DcDropDown,
  DcFlexBox,
  DcText,
  PixelPageContainer,
  Section,
  SectionContent,
  colors,
  PixelFactoryContext,
  DcOverlaySlider,
  type DemandForecastMetric,
} from 'pixel-base'
import type { AjaxResponse } from 'rxjs/ajax'
import styled from 'styled-components'
import {
  getDemandForecast,
  type DemandForecastItem,
  type DemandForecastResponse,
} from 'utils/api-routes-planning'
import { ArrowRightFromBracketIcon } from 'utils/icons/arrow-right-from-bracket-icon'
import { BufferStockIcon } from 'utils/icons/buffer-stock-icon'
import { CalendarIcon } from 'utils/icons/calendar-icon'
import { GrowthFactorIcon } from 'utils/icons/growth-factor-icon'
import { LeadTimeIcon } from 'utils/icons/lead-time-icon'
import { MoqIcon } from 'utils/icons/moq-icon'
import { ProductTypeIcon } from 'utils/icons/product-type-icon'
import { SalesQuantityIcon } from 'utils/icons/sales-quantity-icon'
import StoreIcon from 'utils/icons/store-icon'
import { TotalProductsIcon } from 'utils/icons/total-products-icon'
import { TruckIcon } from 'utils/icons/truck-icon'
import { WarehouseIcon } from 'utils/icons/warehouse-icon'
import * as XLSX from 'xlsx'
import BomDetail from './components/BomDetail'

const breadcrumbs = [
  {
    name: 'Production Planning',
    route: '/planning',
  },
  {
    name: 'Demand & Forecasting',
    active: true,
  },
]

const MONTH_OPTIONS = [
  { label: 'January', value: '1' },
  { label: 'February', value: '2' },
  { label: 'March', value: '3' },
  { label: 'April', value: '4' },
  { label: 'May', value: '5' },
  { label: 'June', value: '6' },
  { label: 'July', value: '7' },
  { label: 'August', value: '8' },
  { label: 'September', value: '9' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' },
]

const YEAR_OPTIONS = (() => {
  const currentYear = new Date().getFullYear()
  const years: { label: string; value: string }[] = []
  for (let offset = -2; offset <= 2; offset++) {
    const year = (currentYear + offset).toString()
    years.push({ label: year, value: year })
  }
  return years
})()

const CHANNEL_OPTIONS = [
  { label: 'All Sales Channels', value: '' },
  { label: 'E-Commerce', value: 'e-commerce' },
  { label: 'Distribution', value: 'distribution' },
  { label: 'Retail', value: 'retail' },
]

const PRODUCT_TYPE_OPTIONS = [
  { label: 'Finished Goods (FG)', value: 'FG' },
  { label: 'Raw Materials (RM)', value: 'RM' },
  { label: 'Select Products Type', value: '' },
]

const StyledLoaderContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 500px;
  position: relative;
`

const CustomLoader = () => {
  const ref = React.useRef<HTMLDivElement>(null)
  const { showLoader, hideLoader } = React.useContext(PixelFactoryContext) as {
    showLoader: (ref: React.RefObject<HTMLDivElement>) => void
    hideLoader: (ref: React.RefObject<HTMLDivElement>) => void
  }

  React.useEffect(() => {
    if (ref.current) {
      showLoader(ref)
    }
    return () => {
      hideLoader(ref)
    }
  }, [showLoader, hideLoader])
  return (
    <React.Fragment>
      <StyledLoaderContainer className="pixeltable" ref={ref} />
    </React.Fragment>
  )
}

const DemandForecasting: React.FC = () => {
  const { showFailure, showSuccess } = React.useContext(
    PixelFactoryContext,
  ) as {
    showFailure: (_message: string) => void
    showSuccess: (_message: string) => void
  }
  const [showBomDetail, setShowBomDetail] = React.useState(false)
  const [bomDetailHeader, setBomDetailHeader] =
    React.useState<DemandForecastItem | null>(null)
  const currentDate = new Date()
  const nextMonth = currentDate.getMonth() + 2
  const initialMonth = nextMonth > 12 ? 1 : nextMonth
  const initialYear =
    nextMonth > 12 ? currentDate.getFullYear() + 1 : currentDate.getFullYear()
  const [rows, setRows] = React.useState<DemandForecastItem[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [selectedMonth, setSelectedMonth] = React.useState<string>(
    initialMonth.toString(),
  )
  const [selectedYear, setSelectedYear] = React.useState<string>(
    initialYear.toString(),
  )
  const [selectedChannel, setSelectedChannel] = React.useState<string>('')
  const [selectedProductType, setSelectedProductType] =
    React.useState<string>('FG')
  const [showOnlyRequired, setShowOnlyRequired] = React.useState(false)
  const [meta, setMeta] = React.useState<
    DemandForecastResponse['_meta'] | null
  >(null)

  const fetchDemandForecast = React.useCallback(() => {
    setIsLoading(true)
    const filters: Record<string, string | number> = {
      year: Number(selectedYear),
      month: Number(selectedMonth),
    }

    if (selectedChannel) {
      filters.sale_channel = selectedChannel
    }
    if (selectedProductType) {
      filters.product_type = selectedProductType
    }

    const subscription = getDemandForecast(filters).subscribe({
      next: (res: AjaxResponse<DemandForecastResponse>) => {
        if (res.response?.error) {
          showFailure(res.response.error)
          setRows([])
          setMeta(null)
          setIsLoading(false)
          return
        }

        setRows(res.response?.data || [])
        setMeta(res.response?._meta || null)
        setIsLoading(false)
      },
      error: () => {
        showFailure('Failed to fetch demand forecast data')
        setRows([])
        setMeta(null)
        setIsLoading(false)
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [
    selectedMonth,
    selectedYear,
    selectedChannel,
    selectedProductType,
    showFailure,
  ])

  React.useEffect(() => {
    const cleanup = fetchDemandForecast()
    return cleanup
  }, [fetchDemandForecast])

  const filteredRows = React.useMemo(() => {
    if (!showOnlyRequired) return rows
    return rows.filter((row) => (row.required_stock ?? 0) > 0)
  }, [rows, showOnlyRequired])

  const exportToExcel = () => {
    if (filteredRows.length === 0) {
      showFailure('No data to export')
      return
    }

    const exportData = filteredRows.map((row) => {
      const warehouseBreakdown = row.warehouse_stock?.warehouses
        ? row.warehouse_stock.warehouses
            .map((w) => `${w.warehouse_name}: ${w.stock}`)
            .join('; ')
        : ''

      return {
        'Product Name': row.product_name || '',
        'Product SKU': row.product_sku || '',
        'Lead Time (days)': row.lead_time ?? 0,
        MOQ: row.moq ?? 0,
        'Avg Sales (6M)': Math.round(row.avg_sales_6m ?? 0),
        'Growth Factor': row.growth_factor ?? 0,
        'Expected Sales': Math.round(row.expected_sales ?? 0),
        'Warehouse Stock (Total)': row.warehouse_stock?.total ?? 0,
        'In-Transit Stock': row.in_transit_stock ?? 0,
        'Required Stock': Math.round(row.required_stock ?? 0),
        'Warehouse Breakdown': warehouseBreakdown,
      }
    })

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Demand Forecast')

    const monthName =
      MONTH_OPTIONS.find((m) => m.value === selectedMonth)?.label ||
      selectedMonth
    const fileName = `demand_forecast_${monthName}_${selectedYear}.xlsx`
    XLSX.writeFile(workbook, fileName)
    showSuccess('Export successful')
  }

  return (
    <>
      <DcFlexBox
        height="60px"
        style={{
          fontSize: '16px',
          padding: '8px 0px',
          borderBottom: '1px solid #e5e5e5',
        }}
      >
        <DcFlexBox
          justifyContent="space-between"
          alignItems="center"
          padding=".5rem 20px"
          width="100%"
        >
          <DcBreadcrumbs
            className="breadcrumbs"
            fontSize="16px"
            data={breadcrumbs}
          />
          <ProfileSection />
        </DcFlexBox>
      </DcFlexBox>

      <PixelPageContainer padding="0px" height="calc(100vh - 61px)">
        <Section style={{ padding: '0px' }}>
          <SectionContent style={{ padding: '5px 20px' }}>
            <DcFlexBox
              height="auto"
              width="100%"
              justifyContent="space-between"
              flexDirection="row-reverse"
            >
              <DcFlexBox height="auto" width="auto" gap="10px">
                <DcButton
                  variant="outline-secondary"
                  size="sm"
                  onClick={exportToExcel}
                  disabled={rows.length === 0}
                  withIcon={true}
                >
                  <ArrowRightFromBracketIcon size={16} />
                  <span>Export</span>
                </DcButton>
              </DcFlexBox>
              <DcFlexBox width="70%" gap="5px" height="auto">
                <DcDropDown
                  dropdownSize="sm"
                  options={MONTH_OPTIONS}
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                />
                <DcDropDown
                  dropdownSize="sm"
                  options={YEAR_OPTIONS}
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                />
                <DcDropDown
                  dropdownSize="sm"
                  options={CHANNEL_OPTIONS}
                  value={selectedChannel}
                  onChange={(e) => setSelectedChannel(e.target.value)}
                />
                <DcDropDown
                  dropdownSize="sm"
                  options={PRODUCT_TYPE_OPTIONS}
                  value={selectedProductType}
                  onChange={(e) => setSelectedProductType(e.target.value)}
                />
              </DcFlexBox>
            </DcFlexBox>
          </SectionContent>
        </Section>
        &nbsp;
        {meta && (
          <Section style={{ padding: '0px 20px 10px' }}>
            <SectionContent>
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  background: '#ffffff',
                  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '16px',
                  }}
                >
                  {/* Row 1 */}
                  <DcFlexBox alignItems="center" gap="10px" height="auto">
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: colors.primaryLight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <TotalProductsIcon color={colors.primary} size={32} />
                    </div>
                    <div>
                      <DcText style={{ fontSize: '11px', color: '#6b7280' }}>
                        Total Products
                      </DcText>
                      <DcText
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: colors.primary,
                        }}
                      >
                        {meta.total_products}
                      </DcText>
                    </div>
                  </DcFlexBox>
                  <DcFlexBox alignItems="center" gap="10px" height="auto">
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(124, 58, 237, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <GrowthFactorIcon color="#7c3aed" size={32} />
                    </div>
                    <div>
                      <DcText style={{ fontSize: '11px', color: '#6b7280' }}>
                        Growth Factor
                      </DcText>
                      <DcText
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#7c3aed',
                        }}
                      >
                        {meta.growth_factor}
                      </DcText>
                    </div>
                  </DcFlexBox>
                  <DcFlexBox alignItems="center" gap="10px" height="auto">
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(14, 165, 233, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <CalendarIcon color="#0ea5e9" size={32} />
                    </div>
                    <div>
                      <DcText style={{ fontSize: '11px', color: '#6b7280' }}>
                        Target Period
                      </DcText>
                      <DcText
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#0ea5e9',
                        }}
                      >
                        {MONTH_OPTIONS.find(
                          (m) => m.value === meta.target_month.toString(),
                        )?.label || meta.target_month}{' '}
                        {meta.target_year}
                      </DcText>
                    </div>
                  </DcFlexBox>
                  {/* Row 2 */}
                  <DcFlexBox alignItems="center" gap="10px" height="auto">
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(22, 163, 74, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <StoreIcon color="#16a34a" size={32} />
                    </div>
                    <div>
                      <DcText style={{ fontSize: '11px', color: '#6b7280' }}>
                        Sales Channel
                      </DcText>
                      <DcText
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#16a34a',
                          textTransform: 'capitalize',
                        }}
                      >
                        {meta.sale_channel}
                      </DcText>
                    </div>
                  </DcFlexBox>
                  <DcFlexBox alignItems="center" gap="10px" height="auto">
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(245, 158, 11, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <ProductTypeIcon color="#f59e0b" size={32} />
                    </div>
                    <div>
                      <DcText style={{ fontSize: '11px', color: '#6b7280' }}>
                        Product Type
                      </DcText>
                      <DcText
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#f59e0b',
                        }}
                      >
                        {meta.product_type}
                      </DcText>
                    </div>
                  </DcFlexBox>
                  {/* Row 2 - Column 3: Filter Toggle */}
                  <DcFlexBox
                    alignItems="center"
                    gap="10px"
                    height="auto"
                    style={{
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '8px',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                    onClick={() => setShowOnlyRequired(!showOnlyRequired)}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: showOnlyRequired
                          ? 'rgba(244, 63, 94, 0.12)'
                          : 'rgba(203, 213, 225, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <DcCheckbox
                        checked={showOnlyRequired}
                        onChange={() => {}} // Handled by parent onClick
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                    <div>
                      <DcText
                        style={{
                          fontSize: '11px',
                          color: '#6b7280',
                          marginBottom: '2px',
                        }}
                      >
                        Requirement View
                      </DcText>
                      <DcText
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: showOnlyRequired ? '#f43f5e' : '#64748b',
                          transition: 'color 0.2s ease',
                        }}
                      >
                        {showOnlyRequired ? 'Required Stock' : 'Show All'}
                      </DcText>
                    </div>
                  </DcFlexBox>
                </div>
              </div>
            </SectionContent>
          </Section>
        )}
        <Section style={{ padding: '0px 20px 20px' }}>
          <SectionContent
            height="calc(100vh - 300px)"
            style={{ overflow: 'auto', position: 'relative' }}
          >
            {isLoading ? (
              <CustomLoader />
            ) : filteredRows.length === 0 ? (
              <DcText style={{ fontSize: '13px', color: '#6b7280' }}>
                {showOnlyRequired
                  ? 'No products found with required stock.'
                  : 'No demand forecast data found. Try adjusting your filters.'}
              </DcText>
            ) : (
              <DcFlexBox
                width="100%"
                height="auto"
                flexDirection="row"
                gap="12px"
                flexWrap="wrap"
              >
                {filteredRows.map((row) => {
                  const metrics: DemandForecastMetric[] = [
                    {
                      icon: SalesQuantityIcon,
                      label: 'Avg Sales (6M)',
                      value: Math.round(row.avg_sales_6m ?? 0),
                      color: colors.primary,
                      bg: colors.primaryLight,
                    },
                    {
                      icon: GrowthFactorIcon,
                      label: 'Growth Factor',
                      value: row.growth_factor ?? 0,
                      color: '#7c3aed',
                      bg: 'rgba(124, 58, 237, 0.12)',
                    },
                    {
                      icon: CalendarIcon,
                      label: 'Expected Sales',
                      value: Math.round(row.expected_sales ?? 0),
                      color: '#0ea5e9',
                      bg: 'rgba(14, 165, 233, 0.12)',
                    },
                    {
                      icon: WarehouseIcon,
                      label: 'In Stock',
                      value: row.warehouse_stock?.total ?? 0,
                      color: '#16a34a',
                      bg: 'rgba(22, 163, 74, 0.12)',
                    },
                    {
                      icon: TruckIcon,
                      label: 'In Transit',
                      value: row.in_transit_stock ?? 0,
                      color: '#6366f1',
                      bg: 'rgba(99, 102, 241, 0.12)',
                    },
                    {
                      icon: BufferStockIcon,
                      label: 'Required Stock',
                      value: Math.round(row.required_stock ?? 0),
                      color: '#f59e0b',
                      bg: 'rgba(245, 158, 11, 0.12)',
                    },
                  ]

                  return (
                    <DcDemandForecastCard
                      key={row.product_sku}
                      productName={row.product_name}
                      productSku={row.product_sku}
                      productImage={row.product_image}
                      leadTime={row.lead_time}
                      moq={row.moq}
                      leadTimeIcon={LeadTimeIcon}
                      moqIcon={MoqIcon}
                      metrics={metrics}
                      warehouses={row.warehouse_stock?.warehouses}
                      onClick={() => {
                        setShowBomDetail(true)
                        setBomDetailHeader(row as DemandForecastItem)
                      }}
                    />
                  )
                })}
              </DcFlexBox>
            )}
          </SectionContent>
        </Section>
      </PixelPageContainer>
      <DcOverlaySlider
        isOpen={showBomDetail}
        onClose={() => setShowBomDetail(false)}
        title="BOM Detail"
        width="50%"
        padding="20px"
      >
        {bomDetailHeader && <BomDetail header={bomDetailHeader} />}
      </DcOverlaySlider>
    </>
  )
}

export default DemandForecasting
